import { BASES_SUBJECTS, MARKED_LINE_BASES_SUBJECTS, SUBJECT_MAP } from "@/lib/domain-constants";
import type { SearchProfile } from "@/types/domain";

// ---- CUÁNDO AVISA UNA LÍNEA ----
// Espejo de `notifyNewMatches` / `notifyStageChangeMatches` en
// `backend/src/services/userinterest.service.ts`: solo avisa lo que coincide con un
// recurso o un ítem activo. Si la regla cambia allá y no acá, la tarjeta le promete
// al usuario avisos que no le llegan.

// La línea que no mira la convocatoria se avisa al entrar la obra a una de sus etapas
// (una sola vez). En el orden en que pasan, con cómo se dicen: "quede desierta una obra".
const LATER_STAGES: Array<[stage: string, verb: string]> = [
  ["EN_EVALUACION", "pase a evaluación"],
  ["ADJUDICADO", "se adjudique"],
  ["APELADO", "se apele"],
  ["CONSENTIDO", "quede consentida"],
  ["CONTRATADO", "se contrate"],
  ["DESIERTO", "quede desierta"],
  ["SUSPENDIDO", "se suspenda"],
  ["RETROTRAIDO_POR_RESOLUCION", "se retrotraiga"],
  ["REGISTRO_DE_EFECTO_NO_CULMINADO", "quede sin efecto"],
  ["NULO", "se declare nula"],
  ["CANCELADO", "se cancele"],
  ["OTRO", "pase a otra etapa"],
];

/** "a", "a o b", "a, b o c". */
function joinOr(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} o ${parts.at(-1)}`;
}

// Una línea grande tiene decenas de términos: la frase nombra los primeros y la
// tarjeta despliega la lista entera aparte.
const TERMS_IN_SENTENCE = 3;

const isLongList = (terms: string[]) => terms.length > TERMS_IN_SENTENCE + 1;

/** Los términos como se ven en la frase: «cemento» o «asfalto»; con muchos, «a», «b», «c» u otro de tus 31 ítems. */
function quoted(terms: string[], noun: string): string {
  const marks = terms.map((term) => `«${term.trim()}»`);
  if (!isLongList(terms)) return joinOr(marks);
  return `${marks.slice(0, TERMS_IN_SENTENCE).join(", ")} u otro de tus ${marks.length} ${noun}`;
}

const compactSoles = new Intl.NumberFormat("es-PE", { notation: "compact" });

/** " de S/ 1 M a S/ 5 M", " de hasta S/ 1 M"; vacío sin rango. */
function amountPhrase(min?: number | null, max?: number | null): string {
  const from = min && min > 0 ? `S/ ${compactSoles.format(min)}` : null;
  const to = max != null ? `S/ ${compactSoles.format(max)}` : null;
  if (from && to) return ` de ${from} a ${to}`;
  if (from) return ` de más de ${from}`;
  if (to) return ` de hasta ${to}`;
  return "";
}

/** "una obra", "una licitación de bienes o servicios"… Solo hay presupuesto en las obras. */
function subjectNoun(subjects: string[], onlyBudget: boolean): string {
  if (onlyBudget || (subjects.length === 1 && subjects[0] === "works")) return "una obra";
  if (subjects.length === 1 && subjects[0] === "consulting") return "una consultoría de obra";
  if (subjects.length === 0) return "una licitación";
  return `una licitación de ${joinOr(subjects.map((s) => (SUBJECT_MAP[s] ?? s).toLowerCase()))}`;
}

export type LineAlerts = {
  /** `null` cuando la línea no avisa nada: va solo el `warning`. */
  sentence: string | null;
  warning: string | null;
  /** Las listas que la frase resume, para desplegarlas enteras. Vacío si caben en la frase. */
  termGroups: Array<{ label: string; terms: string[] }>;
};

/**
 * La frase de "Avisos de esta línea": cuándo avisa y qué tiene que cumplir la
 * licitación. `zone` y `entity` llegan ya resueltos ("Cusco y Puno", "MUNICIPALIDAD
 * DE…"); vacíos si la línea no filtra por ellos.
 */
export function describeLineAlerts({
  profile,
  resourceTerms,
  itemTerms,
  zone,
  entity,
}: {
  profile: SearchProfile;
  /** Solo los activos. */
  resourceTerms: string[];
  itemTerms: string[];
  zone: string;
  entity: string;
}): LineAlerts {
  // La línea que postula no busca en el presupuesto: sus recursos no matchean.
  const resources = profile.searchprofile_checkqualification ? [] : resourceTerms;
  if (resources.length === 0 && itemTerms.length === 0) {
    return {
      sentence: null,
      warning: "Esta línea no tiene términos activos: no te llegará ningún aviso.",
      termGroups: [],
    };
  }

  const stages = profile.searchprofile_stages ?? [];
  const atConvocatoria = stages.length === 0 || stages.includes("CONVOCADO");
  const verb = atConvocatoria
    ? "se convoque"
    : joinOr(LATER_STAGES.filter(([stage]) => stages.includes(stage)).map(([, said]) => said)) ||
      "pase a otra etapa";

  const inBudget =
    resources.length > 0 ? `lleve ${quoted(resources, "recursos")} en su presupuesto` : "";
  const inDescription =
    itemTerms.length > 0 ? `diga ${quoted(itemTerms, "ítems")} en su descripción` : "";
  const terms = ` que ${[inBudget, inDescription].filter(Boolean).join(" o ")}`;

  const sentence =
    `Te avisamos cuando ${verb} ${subjectNoun(
      profile.searchprofile_subjects ?? [],
      resources.length > 0 && itemTerms.length === 0,
    )}` +
    (profile.searchprofile_scope === "minor" ? " de hasta 8 UIT" : "") +
    (zone ? ` en ${zone}` : "") +
    (entity ? ` de ${entity}` : "") +
    amountPhrase(profile.searchprofile_amountmin, profile.searchprofile_amountmax) +
    (profile.searchprofile_search?.trim()
      ? ` que mencione «${profile.searchprofile_search.trim()}»`
      : "") +
    terms +
    ".";

  return {
    sentence,
    // Sin "Solo mis intereses" la lista muestra más de lo que se avisa.
    warning: profile.searchprofile_onlyinterests
      ? null
      : "La lista te muestra más, pero solo te avisamos lo que coincide con tus términos.",
    termGroups: [
      { label: "En el presupuesto", terms: resources },
      { label: "En la descripción", terms: itemTerms },
    ].filter((group) => isLongList(group.terms)),
  };
}

/**
 * La frase del cruce automático en esa misma tarjeta, o `null` si la línea no cruza
 * nada (solo objetos cuyas bases no se leen). Lo que impide marcarla llega del
 * backend (`analysisBlocker`).
 */
export function describeLineQualification({
  profile,
  blockedReason,
}: {
  profile: SearchProfile;
  blockedReason: string | null;
}): string | null {
  const subjects = profile.searchprofile_subjects ?? [];
  const crossable = [...BASES_SUBJECTS, ...MARKED_LINE_BASES_SUBJECTS];
  if (subjects.length > 0 && !subjects.some((subject) => crossable.includes(subject))) {
    return null;
  }
  // Solo en convocatoria el registro está abierto: después no hay a qué postular.
  const stages = profile.searchprofile_stages ?? [];
  if (stages.length > 0 && !stages.includes("CONVOCADO")) {
    return "No se cruzan: cuando llegan a esta etapa el registro ya cerró.";
  }
  if (blockedReason) return blockedReason;
  return profile.searchprofile_checkqualification
    ? "Las que coincidan con tus ítems se cruzan solas apenas leemos sus bases."
    : "Márcala en Configuración → Documentos para que las que coincidan con tus ítems se crucen solas.";
}
